// modules/Battle/BattleUITargetSelection.js
// 战斗UI目标选择模块

export default class BattleUITargetSelection {
    constructor(scene) {
        this.scene = scene;
        this.elements = {
            target: {}
        };
        
        this.selectingTarget = false;
        this.selectingRangeTarget = false;
        this.selectedTargetIndex = 0;
        this.rangeTargetIndices = [];
    }
    
    /**
     * 创建目标指示器
     */
    createTargetIndicator() {
        // 目标选择箭头
        this.elements.target.arrow = this.scene.add.text(
            0, 0,
            '▶',
            {
                fontSize: '20px',
                fill: '#ffffff',
                fontFamily: 'monospace'
            }
        ).setVisible(false);
        
        // 目标信息文本
        this.elements.target.info = this.scene.add.text(
            this.scene.cameras.main.centerX,
            30,
            '',
            {
                fontSize: '16px',
                fill: '#ffffff',
                fontFamily: 'monospace',
                backgroundColor: '#000000',
                padding: { left: 15, right: 15, top: 8, bottom: 8 }
            }
        ).setOrigin(0.5, 0.5).setVisible(false);
        
        // 范围攻击指示器
        this.elements.target.rangeIndicators = [];
    }
    
    /**
     * 显示目标选择
     */
    showTargetSelection() {
        this.selectingTarget = true;
        this.selectingRangeTarget = false;
        this.selectedTargetIndex = 0;
        
        // 找到第一个活着的敌人
        const aliveEnemies = this.scene.battleEnemy.getAliveEnemies();
        if (aliveEnemies.length > 0) {
            this.selectedTargetIndex = 0;
            this.updateTargetIndicator(aliveEnemies[0]);
            this.updateTargetInfo(aliveEnemies[0]);
        }
    }
    
    /**
     * 显示范围目标选择
     * @param {number} range - 攻击范围
     */
    showRangeTargetSelection(range = 2) {
        this.selectingTarget = false;
        this.selectingRangeTarget = true;
        this.selectedTargetIndex = 0;
        
        // 找到第一个活着的敌人作为中心
        const aliveEnemies = this.scene.battleEnemy.getAliveEnemies();
        if (aliveEnemies.length > 0) {
            this.selectedTargetIndex = 0;
            this.updateRangeTargetSelection(range);
        }
    }
    
    /**
     * 移动目标选择
     * @param {number} direction - 方向 (-1 上, 1 下)
     */
    moveTargetSelection(direction) {
        if (this.selectingRangeTarget) {
            this.moveRangeTargetSelection(direction);
            return;
        }
        
        const aliveEnemies = this.scene.battleEnemy.getAliveEnemies();
        if (aliveEnemies.length === 0) return;
        
        let newIndex = this.selectedTargetIndex;
        let attempts = 0;
        
        // 找到下一个活着的敌人
        do {
            newIndex = (newIndex + direction + aliveEnemies.length) % aliveEnemies.length;
            attempts++;
            
            if (attempts >= aliveEnemies.length * 2) {
                return; // 防止无限循环
            }
        } while (!aliveEnemies[newIndex].visible);
        
        this.selectedTargetIndex = newIndex;
        
        // 更新箭头位置
        const target = aliveEnemies[this.selectedTargetIndex];
        this.updateTargetIndicator(target);
        
        // 更新目标信息
        this.updateTargetInfo(target);
    }
    
    /**
     * 移动范围目标选择
     * @param {number} direction - 方向 (-1 上, 1 下)
     */
    moveRangeTargetSelection(direction) {
        const aliveEnemies = this.scene.battleEnemy.getAliveEnemies();
        if (aliveEnemies.length === 0) return;
        
        let newIndex = this.selectedTargetIndex;
        let attempts = 0;
        
        do {
            newIndex = (newIndex + direction + aliveEnemies.length) % aliveEnemies.length;
            attempts++;
            
            if (attempts >= aliveEnemies.length * 2) return;
        } while (!aliveEnemies[newIndex].visible);
        
        this.selectedTargetIndex = newIndex;
        
        // 更新范围选择
        const weapon = this.scene.battlePlayer?.getCurrentWeapon();
        const range = weapon?.range || 2;
        this.updateRangeTargetSelection(range);
    }
    
    /**
     * 更新目标指示器
     * @param {Object} target - 目标敌人精灵
     */
    updateTargetIndicator(target) {
        if (!target) return;
        
        this.elements.target.arrow.setPosition(target.x - 60, target.y);
        this.elements.target.arrow.setVisible(true);
    }
    
    /**
     * 更新目标信息
     * @param {Object} target - 目标敌人精灵
     */
    updateTargetInfo(target) {
        if (!target || !target.enemyData) return;
        
        const enemy = target.enemyData;
        const infoText = `目标: ${enemy.name} | HP: ${enemy.hp}/${enemy.maxHp} | 攻击: ${enemy.attack}`;
        
        this.elements.target.info.setText(infoText);
        this.elements.target.info.setVisible(true);
    }
    
    /**
     * 更新范围目标选择
     * @param {number} range - 攻击范围
     */
    updateRangeTargetSelection(range) {
        // 清除旧的范围指示器
        this.clearRangeIndicators();
        
        const centerEnemy = this.scene.battleEnemy.getEnemyByIndex(this.selectedTargetIndex);
        if (!centerEnemy || !centerEnemy.visible) return;
        
        this.rangeTargetIndices = [];
        
        // 获取范围内的敌人
        for (let i = 0; i < this.scene.battleEnemy.enemySprites.length; i++) {
            if (Math.abs(i - this.selectedTargetIndex) <= range) {
                const enemy = this.scene.battleEnemy.getEnemyByIndex(i);
                if (enemy && enemy.visible && enemy.enemyData.hp > 0) {
                    this.rangeTargetIndices.push(i);
                    
                    // 创建范围指示器
                    const indicator = this.scene.add.graphics();
                    indicator.lineStyle(3, 0x00ff00, 1);
                    indicator.strokeRect(enemy.x - 50, enemy.y - 50, 100, 100);
                    indicator.setDepth(10);
                    
                    // 添加闪烁效果
                    this.scene.tweens.add({
                        targets: indicator,
                        alpha: { from: 1, to: 0.5 },
                        duration: 500,
                        yoyo: true,
                        repeat: -1
                    });
                    
                    this.elements.target.rangeIndicators.push(indicator);
                }
            }
        }
        
        // 显示范围攻击信息
        const weapon = this.scene.battlePlayer?.getCurrentWeapon();
        const weaponName = weapon ? weapon.name : '范围攻击';
        const infoText = `${weaponName} - 范围攻击 (命中 ${this.rangeTargetIndices.length}个目标)`;
        
        this.elements.target.info.setText(infoText);
        this.elements.target.info.setVisible(true);
    }
    
    /**
     * 清除范围指示器
     */
    clearRangeIndicators() {
        this.elements.target.rangeIndicators.forEach(indicator => {
            if (indicator.destroy) indicator.destroy();
        });
        this.elements.target.rangeIndicators = [];
    }
    
    /**
     * 确认目标选择
     * @returns {Object|null} {targetIndex, isRangeAttack, rangeTargetIndices}
     */
    confirmTargetSelection() {
        if (this.selectingRangeTarget) {
            // 范围攻击确认
            if (this.rangeTargetIndices.length > 0) {
                return {
                    targetIndex: this.selectedTargetIndex,
                    isRangeAttack: true,
                    rangeTargetIndices: this.rangeTargetIndices
                };
            }
        } else {
            // 单目标攻击确认
            const aliveEnemies = this.scene.battleEnemy.getAliveEnemies();
            if (this.selectedTargetIndex >= 0 && this.selectedTargetIndex < aliveEnemies.length) {
                const target = aliveEnemies[this.selectedTargetIndex];
                if (target && target.visible) {
                    // 获取目标的原始索引
                    const originalIndex = target.index;
                    return {
                        targetIndex: originalIndex,
                        isRangeAttack: false,
                        rangeTargetIndices: []
                    };
                }
            }
        }
        return null;
    }
    
    /**
     * 清除目标选择
     */
    clearTargetSelection() {
        this.selectingTarget = false;
        this.selectingRangeTarget = false;
        this.elements.target.arrow.setVisible(false);
        this.elements.target.info.setVisible(false);
        this.clearRangeIndicators();
    }
    
    /**
     * 更新箭头动画（用于闪烁效果）
     * @param {number} time - 当前时间
     */
    updateArrowAnimation(time) {
        if (this.selectingTarget && this.elements.target.arrow.visible) {
            // 箭头闪烁效果
            const alpha = 0.8 + 0.2 * Math.sin(time / 200);
            this.elements.target.arrow.setAlpha(alpha);
        }
    }
}

